import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const EMPTY_FORM = { name: '', email: '' };

const AOStaffMaintenance = () => {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await aoAPI.getStaff();
      setStaff(res.data || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditId(s._id);
    setForm({ name: s.name, email: s.email || '' });
    setFormError('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setFormError('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    try {
      setSaving(true);
      setFormError('');
      if (editId) {
        await aoAPI.updateStaff(editId, form);
      } else {
        await aoAPI.createStaff(form);
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setFormError(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this staff member?')) return;
    try {
      await aoAPI.deleteStaff(id);
      await load();
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Failed to deactivate');
    }
  };

  return (
    <Layout pageTitle="Staff Maintenance">
      <div className="p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Staff / In-Charge</h2>
            <p className="text-xs text-gray-500 mt-0.5">Persons responsible for client accounts.</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Staff
          </button>
        </div>

        {/* Inline add / edit form */}
        {showForm && (
          <div className="mb-5 border border-teal-200 bg-teal-50 rounded-xl p-4">
            <p className="text-sm font-semibold text-teal-700 mb-3">
              {editId ? 'Edit Staff' : 'New Staff'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Full name *"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            {formError && <p className="text-xs text-red-500 mt-2">{formError}</p>}
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-60"
              >
                <CheckIcon className="h-4 w-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={cancelForm}
                className="flex items-center gap-1 border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-4 py-1.5 rounded-lg"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        {/* Table */}
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : staff.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No staff found. Add one to get started.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {staff.map(s => (
                  <tr key={s._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{s.name}</td>
                    <td className="px-4 py-3 text-gray-500">{s.email || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                        {s.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEdit(s)}
                          className="text-gray-400 hover:text-teal-600 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeactivate(s._id)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Deactivate"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AOStaffMaintenance;
